import React, { useCallback, useEffect, useState } from 'react';
import api from '@mysimpledb/shared/config/axios';
import useGetUsage from '@mysimpledb/shared/hooks/useGetUsage';
import './UsageWeb.css';

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const amount = bytes / 1024 ** index;
  const digits = index >= 3 ? 2 : index >= 2 ? 1 : 0;

  return `${amount.toFixed(digits)} ${units[index]}`;
}

function formatDateTime(value) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString();
}

function storageWarning(storage) {
  switch (storage?.warningLevel) {
    case 'full':
      return {
        className: 'usage-storage-warning usage-storage-warning--full',
        text: 'Storage is full. Delete files or increase your storage limit before uploading more attachments.',
      };
    case 'critical':
      return {
        className: 'usage-storage-warning usage-storage-warning--critical',
        text: `Storage is critically low. Only ${formatBytes(
          storage.availableBytes
        )} remains.`,
      };
    case 'high':
      return {
        className: 'usage-storage-warning usage-storage-warning--high',
        text: `Storage is almost full. ${formatBytes(
          storage.availableBytes
        )} remains.`,
      };
    case 'warning':
      return {
        className: 'usage-storage-warning usage-storage-warning--warning',
        text: `You are using ${storage.percentUsed}% of your available storage.`,
      };
    default:
      return null;
  }
}

function privacyLabel(entry) {
  if (!entry) return '';
  if (entry.isSensitive) return 'Sensitive';
  if (entry.isPublic) return 'Public';
  return 'Private';
}

function smsActivityLabel(type) {
  switch (type) {
    case 'purchase':
      return 'Credits purchased';
    case 'sms_sent':
      return 'SMS reminder sent';
    case 'refund':
      return 'Refund';
    case 'adjustment':
      return 'Adjustment';
    case 'reversal':
      return 'Reversal';
    default:
      return String(type || 'Activity').replace(/_/g, ' ');
  }
}

function NotesUsageView({ showAccountHeading = true }) {
  const [notesTotal, setNotesTotal] = useState(0);
  const [sms, setSms] = useState({
    creditsRemaining: 0,
    creditsAdded: 0,
    creditsUsed: 0,
  });
  const [smsActivity, setSmsActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadNotesUsage = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [notesResult, billingResult, activityResult] = await Promise.all([
        api.put('/notes/list', {
          searchTerm: '',
          categoryIds: [],
          categoryMatchMode: 'any',
          uncategorizedOnly: false,
          limit: 1,
          offset: 0,
        }),
        api.get('/billing/notes/summary'),
        api.get('/billing/sms/activity', {
          params: { limit: 10 },
        }),
      ]);

      setNotesTotal(Number(notesResult?.data?.total || 0));
      setSms(
        billingResult?.data?.sms || {
          creditsRemaining: 0,
          creditsAdded: 0,
          creditsUsed: 0,
        }
      );
      setSmsActivity(
        Array.isArray(activityResult?.data?.activity)
          ? activityResult.data.activity
          : []
      );
    } catch (err) {
      console.error(
        'Failed to load Notes usage:',
        err?.response?.data || err?.message || err
      );
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Unable to load Notes usage.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotesUsage();
  }, [loadNotesUsage]);

  if (loading) {
    return <div className="usage-dashboard-state">Loading Notes usage…</div>;
  }

  if (error) {
    return (
      <div className="usage-dashboard-state usage-dashboard-state--error">
        <p>{error}</p>
        <button
          type="button"
          className="usage-retry-button"
          onClick={loadNotesUsage}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="usage-notes-section">
      <div className="usage-heading-row">
        <div>
          <h2 className="usage-title">
            {showAccountHeading ? 'Account Usage' : 'Notes & SMS Usage'}
          </h2>
          <p className="usage-subtitle">
            Notes and prepaid SMS reminder credit activity.
          </p>
        </div>
        <button
          type="button"
          className="usage-refresh-button"
          onClick={loadNotesUsage}
        >
          Refresh
        </button>
      </div>

      <div className="usage-summary-grid">
        <article className="usage-summary-card">
          <div className="usage-summary-value">{notesTotal}</div>
          <div className="usage-summary-label">Notes</div>
          <div className="usage-summary-note">Current active notes</div>
        </article>

        <article className="usage-summary-card">
          <div className="usage-summary-value">
            {Number(sms.creditsRemaining || 0)}
          </div>
          <div className="usage-summary-label">SMS credits remaining</div>
          <div className="usage-summary-note">Available for reminders</div>
        </article>

        <article className="usage-summary-card">
          <div className="usage-summary-value">
            {Number(sms.creditsAdded || 0)}
          </div>
          <div className="usage-summary-label">SMS credits purchased</div>
          <div className="usage-summary-note">Total credits added</div>
        </article>

        <article className="usage-summary-card">
          <div className="usage-summary-value">
            {Number(sms.creditsUsed || 0)}
          </div>
          <div className="usage-summary-label">SMS credits used</div>
          <div className="usage-summary-note">Reminders sent</div>
        </article>
      </div>

      <section className="usage-panel">
        <h3>Recent SMS credit activity</h3>

        {smsActivity.length ? (
          <div className="usage-table-wrap">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Description</th>
                  <th>Date</th>
                  <th className="usage-number">Credits</th>
                </tr>
              </thead>
              <tbody>
                {smsActivity.map((row) => (
                  <tr key={row.id}>
                    <td>{smsActivityLabel(row.transaction_type)}</td>
                    <td>{row.description || '—'}</td>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td className="usage-number">
                      {Number(row.credits || 0) > 0 ? '+' : ''}
                      {Number(row.credits || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="usage-empty">No SMS credit activity yet.</p>
        )}
      </section>
    </div>
  );
}

function MySimpleDBUsageView() {
  const token = localStorage.getItem('token');
  const { data, loading, error, refetch } = useGetUsage(token);

  if (loading) {
    return <div className="usage-dashboard-state">Loading MySimpleDB usage…</div>;
  }

  if (error || !data) {
    return (
      <div className="usage-dashboard-state usage-dashboard-state--error">
        <p>Unable to load MySimpleDB usage.</p>
        <button type="button" className="usage-retry-button" onClick={refetch}>
          Try again
        </button>
      </div>
    );
  }

  const storage = data.storage || {};
  const entries = data.entries || {};
  const attachments = data.attachments || {};
  const storageByPrivacy = data.storageByPrivacy || {};
  const largestAttachments = data.largestAttachments || [];
  const largestEntry = data.largestEntry || null;
  const warning = storageWarning(storage);
  const progress = Math.max(0, Math.min(Number(storage.percentUsed || 0), 100));

  return (
    <div className="usage-mysimpledb-section">
      <div className="usage-section-divider" />

      <div className="usage-heading-row">
        <div>
          <h2 className="usage-title">MySimpleDB Usage</h2>
          <p className="usage-subtitle">
            Storage, entries, attachments, and recent account activity.
          </p>
        </div>
        <button type="button" className="usage-refresh-button" onClick={refetch}>
          Refresh
        </button>
      </div>

      <section className="usage-storage-card">
        <div className="usage-storage-header">
          <div>
            <div className="usage-eyebrow">Storage</div>
            <div className="usage-storage-main">
              {formatBytes(storage.usedBytes)} of{' '}
              {formatBytes(storage.limitBytes)} used
            </div>
          </div>
          <div className="usage-storage-percent">
            {storage.percentUsed || 0}%
          </div>
        </div>

        <div
          className="usage-progress"
          role="progressbar"
          aria-label="Storage used"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={progress}
        >
          <div
            className={`usage-progress-fill usage-progress-fill--${
              storage.warningLevel || 'normal'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="usage-storage-details">
          <span>{formatBytes(storage.usedBytes)} used</span>
          <span>{formatBytes(storage.availableBytes)} available</span>
          <span>{formatBytes(storage.limitBytes)} limit</span>
        </div>

        {warning && <div className={warning.className}>{warning.text}</div>}
      </section>

      <div className="usage-summary-grid">
        <article className="usage-summary-card">
          <div className="usage-summary-value">{entries.total || 0}</div>
          <div className="usage-summary-label">Entries</div>
          <div className="usage-summary-note">
            +{entries.thisWeek || 0} this week
          </div>
        </article>

        <article className="usage-summary-card">
          <div className="usage-summary-value">{attachments.total || 0}</div>
          <div className="usage-summary-label">Attachments</div>
          <div className="usage-summary-note">
            +{attachments.thisWeek || 0} this week
          </div>
        </article>

        <article className="usage-summary-card">
          <div className="usage-summary-value">
            {formatBytes(attachments.totalBytes)}
          </div>
          <div className="usage-summary-label">Attachment storage</div>
          <div className="usage-summary-note">Current active attachments</div>
        </article>

        <article className="usage-summary-card">
          <div className="usage-summary-value">
            {largestEntry ? formatBytes(largestEntry.totalBytes) : '0 B'}
          </div>
          <div className="usage-summary-label">Largest entry</div>
          <div className="usage-summary-note usage-summary-note--truncate">
            {largestEntry?.entryTitle || 'No attachments yet'}
          </div>
        </article>
      </div>

      <div className="usage-two-column">
        <section className="usage-panel">
          <h3>Entries</h3>
          <div className="usage-stat-list">
            <div>
              <span>Public</span>
              <strong>{entries.public || 0}</strong>
            </div>
            <div>
              <span>Private</span>
              <strong>{entries.private || 0}</strong>
            </div>
            <div>
              <span>Sensitive</span>
              <strong>{entries.sensitive || 0}</strong>
            </div>
            <div>
              <span>Created this week</span>
              <strong>{entries.thisWeek || 0}</strong>
            </div>
          </div>
        </section>

        <section className="usage-panel">
          <h3>Storage by privacy</h3>
          <div className="usage-stat-list">
            <div>
              <span>Public</span>
              <strong>{formatBytes(storageByPrivacy.publicBytes)}</strong>
            </div>
            <div>
              <span>Private</span>
              <strong>{formatBytes(storageByPrivacy.privateBytes)}</strong>
            </div>
            <div>
              <span>Sensitive</span>
              <strong>{formatBytes(storageByPrivacy.sensitiveBytes)}</strong>
            </div>
          </div>
        </section>
      </div>

      <section className="usage-panel">
        <h3>Attachments by type</h3>
        {attachments.byType?.length ? (
          <div className="usage-table-wrap">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th className="usage-number">Count</th>
                  <th className="usage-number">Storage</th>
                </tr>
              </thead>
              <tbody>
                {attachments.byType.map((row) => (
                  <tr key={row.category}>
                    <td>{row.category}</td>
                    <td className="usage-number">{row.count}</td>
                    <td className="usage-number">{formatBytes(row.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="usage-empty">No attachments yet.</p>
        )}
      </section>

      <section className="usage-panel">
        <h3>Largest attachments</h3>
        {largestAttachments.length ? (
          <div className="usage-table-wrap">
            <table className="usage-table usage-table--largest">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Entry</th>
                  <th>Type</th>
                  <th className="usage-number">Size</th>
                </tr>
              </thead>
              <tbody>
                {largestAttachments.map((file) => (
                  <tr key={file.attachmentId}>
                    <td title={file.filename}>{file.filename}</td>
                    <td title={file.entryTitle}>{file.entryTitle}</td>
                    <td>{file.isSensitive ? 'Sensitive' : file.mimetype}</td>
                    <td className="usage-number">
                      {formatBytes(file.sizeBytes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="usage-empty">No attachments yet.</p>
        )}
      </section>

      {largestEntry && (
        <section className="usage-largest-entry">
          <div>
            <div className="usage-eyebrow">
              Largest entry by attachment storage
            </div>
            <strong>{largestEntry.entryTitle}</strong>
            <div className="usage-largest-entry-meta">
              {largestEntry.attachmentCount} attachment
              {largestEntry.attachmentCount === 1 ? '' : 's'} ·{' '}
              {privacyLabel(largestEntry)}
            </div>
          </div>
          <div className="usage-largest-entry-size">
            {formatBytes(largestEntry.totalBytes)}
          </div>
        </section>
      )}
    </div>
  );
}

export default function UsageWeb() {
  const productCode = localStorage.getItem('productCode');
  const isNotesOnly = productCode === 'notes';
  const hasMySimpleDB = productCode === 'mysimpledb';

  if (isNotesOnly) {
    return (
      <div className="usage-dashboard">
        <NotesUsageView showAccountHeading />
      </div>
    );
  }

  if (hasMySimpleDB) {
    return (
      <div className="usage-dashboard">
        <NotesUsageView showAccountHeading={false} />
        <MySimpleDBUsageView />
      </div>
    );
  }

  return (
    <div className="usage-dashboard-state usage-dashboard-state--error">
      <p>Usage information is not available for this account.</p>
    </div>
  );
}
